import { useMemo } from "react";
import {
  ArrowRight,
  CheckCircle2,
  PencilLine,
  ScrollText,
} from "lucide-react";

import type { DocgenResponse, DocumentSection } from "@/api/schemas";
import { cn } from "@/lib/utils";
import { countHr } from "@/lib/pluralHr";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ifcGroundedFactKeys, perSectionStatus } from "./status";

/**
 * Result header for the delivery screen — replaces the old ConfidencePanel
 * (three competing percentages) + ValidationBar (a red "greška" banner).
 *
 * Reframes the moment as a win with an honest to-do list instead of an
 * anxiety wall. The same validation state used to be told four times, twice
 * negatively; here it's told once, split by what it actually means:
 *
 *  • **Spreman za pregled** — generation succeeded (it always has, if we're
 *    on this screen): section count + elapsed time, led with a green check.
 *  • **Usklađenost s planom** — the grounded proof, promoted from the last
 *    ``Činjenice`` tab. Source: ``fact_sheet.compliance_checks`` (same data
 *    the tab table renders).
 *  • **Za dopuniti** — fields still awaiting the architect (``section.missing``,
 *    structured), NOT validator errors. Jumps to the izjava that owns them.
 *  • **Za provjeru** — real validator errors/warnings, framed as review items.
 *
 * The BIM-grounding % (0 for every DXF project) is demoted to a quiet
 * sub-line detail shown only when it's actually non-zero.
 */

interface ResultSummaryProps {
  data: DocgenResponse;
  onJumpToSection?: (sectionId: string) => void;
}

interface SparkDot {
  section_id: string;
  title: string;
  state: "ok" | "warn" | "err" | "neutral";
  ifcGrounded: boolean;
}

export function ResultSummary({ data, onJumpToSection }: ResultSummaryProps) {
  const tekstualni = useMemo(
    () => data.tekstualni_dio.filter((s) => s.section_type !== "table"),
    [data.tekstualni_dio],
  );
  const allSections = useMemo(
    () => [...data.izjave, ...data.naslovnice, ...tekstualni, ...data.tables],
    [data, tekstualni],
  );

  const totalSections = allSections.length;
  const elapsed = data.elapsed_seconds;

  // "Za dopuniti" — structured per-section fields awaiting a human, distinct
  // from validator findings. Jump target is the first section that owns any.
  const missingCount = useMemo(
    () => allSections.reduce((n, s) => n + (s.missing?.length ?? 0), 0),
    [allSections],
  );
  const firstMissing = useMemo(
    () => allSections.find((s) => (s.missing?.length ?? 0) > 0)?.section_id ?? null,
    [allSections],
  );

  // "Usklađenost s planom" — the grounded compliance proof, promoted up top.
  const checks = data.fact_sheet.compliance_checks ?? [];
  const complianceOk = checks.filter((c) => c.status === "OK").length;
  const complianceBreaches = checks.length - complianceOk;

  // "Za provjeru" — actual validator output.
  const errorCount = data.validation.errors.length;
  const warningCount = data.validation.warnings.length;
  const firstIssue =
    data.validation.errors[0]?.section_id ??
    data.validation.warnings[0]?.section_id ??
    null;

  const nothingPending =
    missingCount === 0 &&
    complianceBreaches === 0 &&
    errorCount === 0 &&
    warningCount === 0;

  // BIM grounding share — quiet detail, shown only when non-zero (DXF = 0).
  const ifcPct = useMemo(() => {
    const llm = collectLlmSections(data);
    let total = 0;
    let ifc = 0;
    for (const s of llm) {
      total += (s.facts_used ?? []).length;
      ifc += ifcGroundedFactKeys(s, data.fact_sheet as Record<string, unknown>).length;
    }
    return total === 0 ? 0 : Math.round((ifc / total) * 100);
  }, [data]);

  const dots = useMemo<SparkDot[]>(() => {
    const llm = collectLlmSections(data);
    const out: SparkDot[] = llm.map((s) => ({
      section_id: s.section_id,
      title: s.title,
      state: perSectionStatus(s.section_id, data.validation).kind,
      ifcGrounded:
        ifcGroundedFactKeys(s, data.fact_sheet as Record<string, unknown>).length >
        0,
    }));
    for (const t of data.tables) {
      out.push({ section_id: t.section_id, title: t.title, state: "neutral", ifcGrounded: false });
    }
    return out;
  }, [data]);

  const subParts = [
    `${countHr(totalSections, "sekcija", "sekcije", "sekcija")}`,
    elapsed !== undefined
      ? `generirano za ${elapsed.toFixed(1).replace(".", ",")} s`
      : null,
    ifcPct > 0 ? `${ifcPct}% iz BIM modela` : null,
  ].filter(Boolean);

  return (
    <div className="sticky top-[58px] z-20 mb-4 rounded-lg border border-stone-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-7 w-7 flex-shrink-0 text-green-600" aria-hidden />
          <div>
            <div className="text-[1.02rem] font-bold leading-tight text-stone-800">
              Glavni projekt spreman za pregled
            </div>
            <div className="text-[0.8rem] text-stone-500">{subParts.join(" · ")}</div>
          </div>
        </div>

        {dots.length > 0 && (
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-1.5" aria-label="Status sekcija">
              {dots.map((dot) => (
                <Tooltip key={dot.section_id}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onJumpToSection?.(dot.section_id)}
                      aria-label={`${dot.title} — ${tooltipForState(dot.state)}`}
                      className={cn(
                        "h-2.5 w-2.5 rounded-full transition-transform hover:scale-125 focus:outline-none focus-visible:ring-2 focus-visible:ring-ind-500 focus-visible:ring-offset-2",
                        dotClass(dot.state),
                        dot.ifcGrounded && "ring-1 ring-offset-1 ring-ind-300",
                      )}
                    />
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <div className="text-[0.78rem] font-medium">{dot.title}</div>
                    <div className="text-[0.72rem] text-white/70">
                      {tooltipForState(dot.state)}
                      {dot.ifcGrounded && " · utemeljeno na IFC"}
                    </div>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
            <div className="flex items-center gap-2 text-[0.66rem] text-stone-400">
              <LegendDot cls="bg-emerald-500" /> OK
              <LegendDot cls="bg-amber-500" /> za pregled
              <LegendDot cls="bg-red-500" /> ispravak
              <LegendDot cls="bg-stone-300" /> tablice
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {checks.length > 0 &&
          (complianceBreaches === 0 ? (
            <Chip tone="ok" icon={<CheckCircle2 className="h-3.5 w-3.5" />}>
              Usklađeno s planom · {complianceOk}/{checks.length} parametara
            </Chip>
          ) : (
            <Chip
              tone="warn"
              icon={<ScrollText className="h-3.5 w-3.5" />}
              title="Detalji u kartici Činjenice"
            >
              Plan: {complianceOk}/{checks.length} OK ·{" "}
              {countHr(
                complianceBreaches,
                "prekoračenje",
                "prekoračenja",
                "prekoračenja",
              )}
            </Chip>
          ))}

        {missingCount > 0 && (
          <Chip
            tone="info"
            icon={<PencilLine className="h-3.5 w-3.5" />}
            onClick={firstMissing ? () => onJumpToSection?.(firstMissing) : undefined}
          >
            Za dopuniti · {countHr(missingCount, "polje", "polja", "polja")}
          </Chip>
        )}

        {errorCount + warningCount > 0 && (
          <Chip
            tone={errorCount > 0 ? "err" : "warn"}
            icon={<ScrollText className="h-3.5 w-3.5" />}
            onClick={firstIssue ? () => onJumpToSection?.(firstIssue) : undefined}
          >
            {errorCount > 0
              ? `${countHr(errorCount, "greška", "greške", "grešaka")} za ispravak`
              : `${warningCount} za pregled`}
            {errorCount > 0 && warningCount > 0 && ` · ${warningCount} napomena`}
          </Chip>
        )}

        {nothingPending && (
          <Chip tone="ok" icon={<CheckCircle2 className="h-3.5 w-3.5" />}>
            Sve provjere prošle
          </Chip>
        )}
      </div>
    </div>
  );
}

type ChipTone = "ok" | "info" | "warn" | "err";

const CHIP_TONE: Record<ChipTone, string> = {
  ok: "border-green-200 bg-green-50 text-green-800",
  info: "border-ind-200 bg-ind-50 text-ind-700",
  warn: "border-amber-200 bg-amber-100 text-amber-800",
  err: "border-red-200 bg-red-50 text-red-700",
};

function Chip({
  tone,
  icon,
  children,
  onClick,
  title,
}: {
  tone: ChipTone;
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick?: () => void;
  title?: string;
}) {
  const className = cn(
    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[0.8rem] font-medium",
    CHIP_TONE[tone],
    onClick && "cursor-pointer transition-[filter] hover:brightness-[0.97]",
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} title={title} className={className}>
        {icon}
        {children}
        <ArrowRight className="h-3 w-3 opacity-70" />
      </button>
    );
  }
  return (
    <span title={title} className={className}>
      {icon}
      {children}
    </span>
  );
}

function LegendDot({ cls }: { cls: string }) {
  return <span className={cn("inline-block h-2 w-2 rounded-full", cls)} aria-hidden />;
}

function dotClass(state: SparkDot["state"]): string {
  switch (state) {
    case "ok":
      return "bg-emerald-500";
    case "warn":
      return "bg-amber-500";
    case "err":
      return "bg-red-500";
    case "neutral":
      return "bg-stone-300";
  }
}

function tooltipForState(state: SparkDot["state"]): string {
  switch (state) {
    case "ok":
      return "Validacija OK";
    case "warn":
      return "Upozorenje validatora";
    case "err":
      return "Greška validatora";
    case "neutral":
      return "Deterministička sekcija";
  }
}

function collectLlmSections(data: DocgenResponse): DocumentSection[] {
  const out: DocumentSection[] = [];
  for (const bucket of [
    data.izjave,
    data.naslovnice,
    data.tekstualni_dio.filter((s) => s.section_type !== "table"),
  ]) {
    for (const s of bucket) {
      if (s.source === "llm" || s.source === "llm_refined") out.push(s);
    }
  }
  return out;
}
